import {
  PATH_STORE_PROJECT_ASSIGNEES_EDIT,
  PATH_STORE_PROJECT_ASSIGNEES_NEW,
} from "@/constants";
import { storeProjectAssigneeListItem } from "@/schemas/storeProjectAssignees";
import { StoreProjectAssigneeListItem } from "@/types/storeProjectAssignees";
import { memo, useMemo, useState, useCallback } from "react";
import { DataTableQueryContextProvider } from "@/components/data-table/DataTableQueryContext";
import { RowSelectionProvider } from "@/components/data-table/context/RowSelectionProvider";
import { useRowSelectionContext } from "@/components/data-table/context/RowSelectionContext";
import { QueryTable } from "@/components/query-table/QueryTable";
import { useStoreProjectAssigneeListQuery } from "@/modules/storeProjectAssignees/hooks/useStoreProjectAssigneeListQuery";
import { useStoreProjectAssigneeBreadcrumbs } from "@/modules/storeProjectAssignees/hooks/useStoreProjectAssigneeBreadcrumbs";
import { useStoreProjectAssigneeColumnOverrides } from "@/modules/storeProjectAssignees/hooks/useStoreProjectAssigneeColumnOverrides";
import { Module } from "@/constants";
import { toCamelCase } from "@/lib/utils";
import AddItemButton from "@/components/AddItemButton";
import PermissionGuard from "@/components/PermissionGuard";
import { Operation } from "@/types/permissions";
import { StoreProjectAssigneesDeleteSelectedButton } from "@/modules/storeProjectAssignees/components/StoreProjectAssigneesDeleteSelectedButton";
import { StoreProjectAssigneesDeleteDialog } from "@/modules/storeProjectAssignees/components/StoreProjectAssigneesDeleteDialog";
import { generatePath } from "react-router-dom";

const StoreProjectAssigneesDirectoryContent = () => {
  const { store } = useRowSelectionContext();
  const { query: storeProjectAssigneeListQuery, options } =
    useStoreProjectAssigneeListQuery();

  const breadcrumbs = useStoreProjectAssigneeBreadcrumbs();
  const columnOverrides = useStoreProjectAssigneeColumnOverrides();
  const name = toCamelCase(Module.StoreProjectAssignees);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  const actions = useMemo(
    () => [
      <PermissionGuard permission={Operation.Create} key="add">
        <AddItemButton
          path={PATH_STORE_PROJECT_ASSIGNEES_NEW}
          labelKey={`${name}.add`}
        />
      </PermissionGuard>,
    ],
    [name],
  );

  const handleDeleteClick = useCallback(() => {
    setIsDeleteDialogOpen(true);
  }, []);

  const handleDeleteSuccess = useCallback(() => {
    store.clear();
    storeProjectAssigneeListQuery.refetch();
  }, [store, storeProjectAssigneeListQuery]);

  return (
    <QueryTable<StoreProjectAssigneeListItem>
      schema={storeProjectAssigneeListItem}
      name={name}
      pathBuilder={(row) =>
        generatePath(PATH_STORE_PROJECT_ASSIGNEES_EDIT, {
          assigneeId: row.id,
        })
      }
      query={storeProjectAssigneeListQuery}
      options={options}
      columnOverrides={columnOverrides}
      tableFeatures={{
        enableRowSelection: true,
        enableSelectAll: true,
        exportSettings: { moduleName: Module.StoreProjectAssignees },
        initialColumnVisibility: {
          invoicing_company_name: false,
          payroll_type_name: false,
          project_start_date: false,
          project_end_date: false,
          project_status: false,
        },
      }}
    >
      <QueryTable.Header
        breadcrumbLabel={`${name}.title.multiple`}
        breadcrumbs={breadcrumbs}
        actions={actions}
      />
      <QueryTable.Menubar>
        <QueryTable.Menubar.Tools />
        <QueryTable.Menubar.Columns />
        <QueryTable.Menubar.Settings />
      </QueryTable.Menubar>
      <DataTableQueryContextProvider
        value={{ query: storeProjectAssigneeListQuery }}
      >
        <QueryTable.Content>
          <QueryTable.Status>
            <QueryTable.SelectedRowsStatus />
            {!storeProjectAssigneeListQuery.isLoading && (
              <StoreProjectAssigneesDeleteSelectedButton
                onClick={handleDeleteClick}
              />
            )}
          </QueryTable.Status>
          <StoreProjectAssigneesDeleteDialog
            open={isDeleteDialogOpen}
            onOpenChange={setIsDeleteDialogOpen}
            onDeleteSuccess={handleDeleteSuccess}
          />
        </QueryTable.Content>
      </DataTableQueryContextProvider>
    </QueryTable>
  );
};

const StoreProjectAssigneesDirectoryComp = () => {
  return (
    <RowSelectionProvider>
      <StoreProjectAssigneesDirectoryContent />
    </RowSelectionProvider>
  );
};

export const StoreProjectAssigneesDirectory = memo(
  StoreProjectAssigneesDirectoryComp,
);
